/**
 * BUG-2026-0137 §8 — pure model for the Rock TCS / UCS "post-peak data" slider
 * on the Analysis & Plots tab.
 *
 * The slider picks the LAST reading index included in the stress–strain plots.
 * Semantics (shared with the backend `PlotCutoffIndex` column):
 *   - stored NULL  = no cutoff → plot ALL data (the default for new uploads);
 *   - stored N     = plot rows 0…N (slider position N).
 * Sliding left removes data from the END of the test; the floor is the peak
 * row (`maxRowIndex`) so the peak itself can never be trimmed away.
 *
 * Rework: "ALL data" excludes the post-FAILURE machine tail — after the
 * specimen breaks the frame unloads but keeps logging (stress collapses to ~0,
 * strain gauges reset). The backend detects that boundary (`plotEndIndex` in
 * the readings payload) and it caps the slider, the default plot, and the
 * report chart alike, so the curves can never dive to zero or double back.
 */

export interface PlotCutoffModel {
  /** Lowest selectable index — the peak-load row (post-peak trim only). */
  min: number;
  /** Highest selectable index — the last REAL reading (= "all data";
   *  post-failure machine-tail rows are past this and never plottable). */
  max: number;
  /** Current slider position (saved cutoff clamped, or `max` when unset). */
  value: number;
}

/**
 * Build the slider model. Returns null when there is nothing to trim
 * (fewer than 2 readings, or the peak is the last plottable row).
 */
export function buildPlotCutoffModel(
  readingCount: number,
  maxRowIndex: number | null | undefined,
  plotCutoffIndex: number | null | undefined,
  plotEndIndex?: number | null,
): PlotCutoffModel | null {
  if (readingCount < 2) return null;
  const max = Math.min(plotEndIndex ?? readingCount - 1, readingCount - 1);
  const min = Math.min(Math.max(maxRowIndex ?? 0, 0), max);
  if (min >= max) return null; // no post-peak rows to trim
  const value =
    plotCutoffIndex === null || plotCutoffIndex === undefined
      ? max
      : Math.min(Math.max(plotCutoffIndex, min), max);
  return { min, max, value };
}

/**
 * Translate a slider position into the value to SAVE: full-right (= all
 * plottable data) is stored as NULL — the canonical "no cutoff", robust
 * against later row edits changing the count. `lastPlottableIndex` is the
 * slider's max (the plot-end boundary, or the last row when there is none).
 */
export function cutoffForSave(sliderValue: number, lastPlottableIndex: number): number | null {
  return sliderValue >= lastPlottableIndex ? null : sliderValue;
}

/** Last plotted index for a draft cutoff (NULL = everything plottable). */
export function effectivePlotEnd(
  plotCutoffIndex: number | null | undefined,
  readingCount: number,
  plotEndIndex?: number | null,
): number {
  if (readingCount === 0) return -1;
  const cap = Math.min(plotEndIndex ?? readingCount - 1, readingCount - 1);
  if (plotCutoffIndex === null || plotCutoffIndex === undefined) return cap;
  return Math.min(Math.max(plotCutoffIndex, 0), cap);
}
